import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';
import { u2107ApplyCategoryBusinessError } from './tools/u2107ApplyCategoryBusinessError';

const LOGICALUNIT_URL = 'U2107';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U2107InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

export interface U2107LogicalUnitCategory {
  id: string;
  code: string;
  labelFr: string;
  labelEn: string;
  sortOrder: number;
  version: number;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

export type U2107LogicalUnitCategorySearchRow = U2107LogicalUnitCategory;

export interface U2107LogicalUnitCategoryCreateBody {
  code: string;
  labelFr: string;
  labelEn: string;
  sortOrder: number;
}

export interface U2107LogicalUnitCategoryUpdateBody {
  id: string;
  version: number;
  labelFr: string;
  labelEn: string;
  sortOrder: number;
}

export interface U2107LogicalUnitCategoryDeleteBody {
  id: string;
  version: number;
}

export function createEmptyU2107Category(): U2107LogicalUnitCategory {
  return {
    id: '',
    code: '',
    labelFr: '',
    labelEn: '',
    sortOrder: 0,
    version: 0,
  };
}

export function buildCreateBody(data: U2107LogicalUnitCategory): U2107LogicalUnitCategoryCreateBody {
  return {
    code: data.code.trim().toUpperCase(),
    labelFr: data.labelFr.trim(),
    labelEn: data.labelEn.trim(),
    sortOrder: Number(data.sortOrder) || 0,
  };
}

export function buildUpdateBody(data: U2107LogicalUnitCategory): U2107LogicalUnitCategoryUpdateBody {
  return {
    id: data.id,
    version: data.version,
    labelFr: data.labelFr.trim(),
    labelEn: data.labelEn.trim(),
    sortOrder: Number(data.sortOrder) || 0,
  };
}

export function buildDeleteBody(data: Pick<U2107LogicalUnitCategory, 'id' | 'version'>): U2107LogicalUnitCategoryDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U2107LogicalUnitCategoryCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U2107LogicalUnitCategoryUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U2107LogicalUnitCategoryDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string };
  u2107ApplyCategoryBusinessError(t, nameSpace, apiErr?.errorCode, errorResult);
}
